import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, Button, Select } from "@repo/o37-ui";
import {
  ArrowLeft,
  Upload,
  FileSpreadsheet,
  CheckCircle,
  AlertCircle,
  Trash2,
  Plus
} from "lucide-react";
import { HoldingsCompany, formatCents } from '../../types';

const API_URL = import.meta.env.DEV ? "http://localhost:3001/api" : "/api";

interface ImportRow {
  companyId: string;
  companyName: string;
  periodType: 'month' | 'quarter';
  periodStart: string;
  revenueCents: number;
  isValid: boolean;
  error?: string;
}

export default function RevenueImport() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState<HoldingsCompany[]>([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{ success: boolean; message: string } | null>(null);

  // Manual entry rows
  const [rows, setRows] = useState<ImportRow[]>([]);

  const userId = localStorage.getItem('app_user_id') || '';
  const userRole = localStorage.getItem('app_role') || 'user';

  useEffect(() => {
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    try {
      const response = await fetch(`${API_URL}/holdings/companies`, {
        headers: { 'x-user-id': userId, 'x-user-role': userRole }
      });
      if (response.ok) {
        const data = await response.json();
        setCompanies(data.filter((c: any) => c.status === 'active').map((c: any) => ({
          id: c.id,
          name: c.name,
          status: c.status
        })));
      }
    } catch (e) {
      console.error('Failed to load companies:', e);
    } finally {
      setLoading(false);
    }
  };

  const addRow = () => {
    setRows([...rows, {
      companyId: '',
      companyName: '',
      periodType: 'month',
      periodStart: '',
      revenueCents: 0,
      isValid: false
    }]);
  };

  const updateRow = (index: number, updates: Partial<ImportRow>) => {
    const newRows = [...rows];
    newRows[index] = { ...newRows[index], ...updates };

    // Validate row
    const row = newRows[index];
    let isValid = true;
    let error = '';

    if (!row.companyId) {
      isValid = false;
      error = 'Select a company';
    } else if (!row.periodStart) {
      isValid = false;
      error = 'Select a period';
    } else if (row.revenueCents <= 0) {
      isValid = false;
      error = 'Enter revenue amount';
    }

    newRows[index].isValid = isValid;
    newRows[index].error = error;

    // Update company name
    if (updates.companyId) {
      const company = companies.find(c => c.id === updates.companyId);
      newRows[index].companyName = company?.name || '';
    }

    setRows(newRows);
  };

  const removeRow = (index: number) => {
    setRows(rows.filter((_, i) => i !== index));
  };

  const handleImport = async () => {
    const validRows = rows.filter(r => r.isValid);
    if (validRows.length === 0) {
      setImportResult({ success: false, message: 'No valid rows to import' });
      return;
    }

    setImporting(true);
    setImportResult(null);

    try {
      const payload = validRows.map(r => ({
        companyId: r.companyId,
        periodType: r.periodType,
        periodStart: r.periodStart,
        periodEnd: calculatePeriodEnd(r.periodStart, r.periodType),
        revenueCents: r.revenueCents,
        source: 'import'
      }));

      const response = await fetch(`${API_URL}/holdings/revenue/import`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userId,
          'x-user-role': userRole
        },
        body: JSON.stringify({ records: payload })
      });

      if (response.ok) {
        const data = await response.json();
        setImportResult({
          success: true,
          message: `Successfully imported ${data.imported || validRows.length} revenue records.`
        });
        setRows([]);
      } else {
        const data = await response.json();
        setImportResult({
          success: false,
          message: data.error || 'Failed to import records'
        });
      }
    } catch (e) {
      setImportResult({
        success: false,
        message: 'Failed to import records. Please try again.'
      });
    } finally {
      setImporting(false);
    }
  };

  const calculatePeriodEnd = (start: string, type: 'month' | 'quarter'): string => {
    if (!start) return '';
    const startDate = new Date(start);
    if (type === 'month') {
      const endDate = new Date(startDate.getFullYear(), startDate.getMonth() + 1, 0);
      return endDate.toISOString().split('T')[0];
    } else {
      const endDate = new Date(startDate.getFullYear(), startDate.getMonth() + 3, 0);
      return endDate.toISOString().split('T')[0];
    }
  };

  const getMonthOptions = () => {
    const options = [];
    const now = new Date();
    for (let i = 0; i < 24; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      options.push({
        value: date.toISOString().split('T')[0],
        label: date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
      });
    }
    return options;
  };

  const getQuarterOptions = () => {
    const options = [];
    const now = new Date();
    const currentQuarter = Math.ceil((now.getMonth() + 1) / 3);
    const currentYear = now.getFullYear();

    for (let i = 0; i < 12; i++) {
      let quarter = currentQuarter - i;
      let year = currentYear;
      while (quarter <= 0) {
        quarter += 4;
        year -= 1;
      }
      const month = (quarter - 1) * 3;
      const date = new Date(year, month, 1);
      options.push({
        value: date.toISOString().split('T')[0],
        label: `Q${quarter} ${year}`
      });
    }
    return options;
  };

  const validRowCount = rows.filter(r => r.isValid).length;
  const totalRevenue = rows.filter(r => r.isValid).reduce((sum, r) => sum + r.revenueCents, 0);

  if (loading) {
    return (
      <div>
        <h1 className="text-2xl font-semibold mb-2">Bulk Import Revenue</h1>
        <p className="text-neutral-500">Loading...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/holdings/revenue')}
          className="flex items-center gap-1.5 text-neutral-500 hover:text-neutral-700 mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Revenue</span>
        </button>

        <h1 className="text-2xl font-semibold mb-1">Bulk Import Revenue</h1>
        <p className="text-neutral-500">Add multiple revenue records at once</p>
      </div>

      {/* Import Result */}
      {importResult && (
        <div className={`mb-6 p-4 rounded-lg flex items-start gap-3 ${
          importResult.success ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'
        }`}>
          {importResult.success ? (
            <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          ) : (
            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          )}
          <div>
            <div className="font-medium">{importResult.success ? 'Success!' : 'Error'}</div>
            <div className="text-sm">{importResult.message}</div>
            {importResult.success && (
              <button
                onClick={() => navigate('/holdings/revenue')}
                className="text-sm underline mt-2"
              >
                View revenue records
              </button>
            )}
          </div>
        </div>
      )}

      {/* Manual Entry Table */}
      <Card className="mb-6">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-neutral-900">Revenue Records</h3>
            <Button
              variant="outline"
              onClick={addRow}
              icon={<Plus className="w-4 h-4" />}
              text="Add Row"
            />
          </div>

          {rows.length === 0 ? (
            <div className="text-center py-8 text-neutral-500">
              <FileSpreadsheet className="w-12 h-12 mx-auto mb-4 text-neutral-300" />
              <p className="mb-4">Add rows to enter revenue data</p>
              <Button
                onClick={addRow}
                icon={<Plus className="w-4 h-4" />}
                text="Add First Row"
              />
            </div>
          ) : (
            <div className="space-y-3">
              {/* Header */}
              <div className="grid grid-cols-12 gap-3 text-xs font-medium text-neutral-500 px-2">
                <div className="col-span-3">Company</div>
                <div className="col-span-2">Type</div>
                <div className="col-span-3">Period</div>
                <div className="col-span-2">Revenue</div>
                <div className="col-span-1">Status</div>
                <div className="col-span-1"></div>
              </div>

              {/* Rows */}
              {rows.map((row, index) => (
                <div key={index} className="grid grid-cols-12 gap-3 items-center p-2 bg-neutral-50 rounded-lg">
                  <div className="col-span-3">
                    <Select
                      value={row.companyId}
                      onChange={(e) => updateRow(index, { companyId: e.target.value })}
                      className="h-9 text-sm"
                    >
                      <option value="">Select...</option>
                      {companies.map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </Select>
                  </div>
                  <div className="col-span-2">
                    <Select
                      value={row.periodType}
                      onChange={(e) => updateRow(index, {
                        periodType: e.target.value as 'month' | 'quarter',
                        periodStart: '' // Reset period when type changes
                      })}
                      className="h-9 text-sm"
                    >
                      <option value="month">Monthly</option>
                      <option value="quarter">Quarterly</option>
                    </Select>
                  </div>
                  <div className="col-span-3">
                    <Select
                      value={row.periodStart}
                      onChange={(e) => updateRow(index, { periodStart: e.target.value })}
                      className="h-9 text-sm"
                    >
                      <option value="">Select...</option>
                      {(row.periodType === 'month' ? getMonthOptions() : getQuarterOptions()).map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </Select>
                  </div>
                  <div className="col-span-2">
                    <div className="relative">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-neutral-400 text-sm">$</span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={row.revenueCents ? (row.revenueCents / 100).toString() : ''}
                        onChange={(e) => updateRow(index, {
                          revenueCents: Math.round(parseFloat(e.target.value || '0') * 100)
                        })}
                        placeholder="0.00"
                        className="w-full h-9 pl-6 pr-2 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                  <div className="col-span-1">
                    {row.isValid ? (
                      <CheckCircle className="w-5 h-5 text-emerald-500" />
                    ) : (
                      <div title={row.error}>
                        <AlertCircle className="w-5 h-5 text-amber-500" />
                      </div>
                    )}
                  </div>
                  <div className="col-span-1">
                    <button
                      onClick={() => removeRow(index)}
                      className="p-1.5 text-neutral-400 hover:text-red-600 hover:bg-red-50 rounded"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Summary */}
          {rows.length > 0 && (
            <div className="mt-4 pt-4 border-t border-neutral-200 flex items-center justify-between">
              <div className="text-sm text-neutral-600">
                <span className="font-medium">{validRowCount}</span> of {rows.length} rows valid
                {validRowCount > 0 && (
                  <span className="ml-4">
                    Total: <span className="font-medium text-emerald-600">{formatCents(totalRevenue)}</span>
                  </span>
                )}
              </div>
              <Button
                onClick={handleImport}
                disabled={importing || validRowCount === 0}
                icon={<Upload className="w-4 h-4" />}
                text={importing ? 'Importing...' : `Import ${validRowCount} Records`}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Instructions */}
      <Card>
        <CardContent className="p-6">
          <h3 className="font-semibold text-neutral-900 mb-3">Instructions</h3>
          <div className="text-sm text-neutral-600 space-y-2">
            <p>
              <strong>1. Add rows:</strong> Click "Add Row" to create entries for each revenue record.
            </p>
            <p>
              <strong>2. Fill in data:</strong> Select the company, period type (monthly/quarterly), period, and revenue amount.
            </p>
            <p>
              <strong>3. Validate:</strong> Each row shows a green checkmark when valid, or an orange warning if incomplete.
            </p>
            <p>
              <strong>4. Import:</strong> Click "Import Records" to save all valid rows at once.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
